import { Navigate, Outlet, useNavigate } from 'react-router';
import { ArrowLeftRight, Building2, CreditCard, Gauge, Layers, ScrollText, ShieldHalf, Users } from 'lucide-react';
import { BRAND, PLATFORM_ROLES } from '@zuri/shared';
import { useSession } from '../lib/session';
import { AppShell, type NavItem } from './AppShell';
import { PlatformMark, useBrandTheme } from '../components/Brand';
import { EmptyState } from '../components/ui/States';
import { Button } from '../components/ui/Button';

/** Ambiente administrativo do SaaS — identidade neutra, rotas e navegação separadas das empresas. */
export function AdminLayout() {
  const { me } = useSession();
  const navigate = useNavigate();
  useBrandTheme(null, 'Plataforma');
  if (!me) return <Navigate to="/entrar" replace />;
  if (!me.platformRole) {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <div className="app-backdrop" aria-hidden />
        <div className="card w-full max-w-md">
          <EmptyState title="Área restrita" description="Esta área é exclusiva da administração da plataforma." action={<Button onClick={() => navigate('/app')}>Voltar</Button>} />
        </div>
      </div>
    );
  }
  const items: NavItem[] = [
    { to: '/admin', end: true, label: 'Visão geral', short: 'Início', icon: <Gauge />, primary: true },
    { to: '/admin/empresas', label: 'Empresas', icon: <Building2 />, primary: true },
    { to: '/admin/assinaturas', label: 'Assinaturas', icon: <CreditCard />, primary: true },
    { to: '/admin/planos', label: 'Planos', icon: <Layers />, primary: true },
    { to: '/admin/usuarios', label: 'Usuários', icon: <Users /> },
    { to: '/admin/auditoria', label: 'Auditoria', icon: <ScrollText /> },
  ];
  const header = (
    <div className="flex items-center gap-2.5 px-2 py-1">
      <PlatformMark className="size-9 shrink-0" />
      <div className="min-w-0">
        <div className="truncate text-[14px] font-[640] leading-tight">{BRAND.name}</div>
        <div className="truncate text-[12px] text-ink-3">{PLATFORM_ROLES[me.platformRole].name}</div>
      </div>
    </div>
  );
  const context = (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-[12px] bg-sunken px-3 py-2 text-[12.5px] font-[560] text-ink-2 shadow-[inset_0_0_0_1px_var(--separator)]">
        <ShieldHalf className="size-4 shrink-0" aria-hidden />
        Administração da plataforma
      </div>
      {me.memberships.length > 0 && (
        <Button size="sm" variant="ghost" className="w-full justify-start" icon={<ArrowLeftRight />} onClick={() => navigate(me.activeCompany ? '/app' : '/empresas')}>
          Ir para área da empresa
        </Button>
      )}
    </div>
  );
  return (
    <AppShell
      header={header}
      contextLabel={context}
      nav={[{ title: 'Administração', items }]}
      mobileTitle={
        <div className="flex min-w-0 items-center gap-2.5">
          <PlatformMark className="size-7 shrink-0" />
          <span className="truncate text-[15px] font-[620]">Plataforma</span>
        </div>
      }
    >
      <Outlet />
    </AppShell>
  );
}
